import type { ReactNode } from "react";
import { cx } from "../cx";

export interface KeyValueItem {
  key: string;
  label: ReactNode;
  value: ReactNode;
  /** Colour the value: a link-like accent, or ok/warn/danger. */
  tone?: "ink" | "accent" | "ok" | "warn" | "danger";
}

const TONE = {
  ink: "text-ink",
  accent: "text-accent",
  ok: "text-ok",
  warn: "text-warn",
  danger: "text-danger",
} as const;

/**
 * Mono label/value rows. `between` pushes the value to the right edge (parse
 * report, spent so far); `columns` keeps a fixed label column (style sheet).
 */
export function KeyValueList({
  items,
  layout = "between",
  labelWidth = 90,
  size = "data",
  className = "",
}: {
  items: ReadonlyArray<KeyValueItem>;
  layout?: "between" | "columns";
  labelWidth?: number;
  size?: "label" | "data";
  className?: string;
}) {
  return (
    <dl
      data-component="KeyValueList"
      className={cx(
        "m-0 flex flex-col gap-1.5 font-mono",
        size === "label" ? "text-label" : "text-data",
        className,
      )}
    >
      {items.map((item) => (
        <div
          key={item.key}
          className={cx("flex gap-3", layout === "between" ? "justify-between" : "items-baseline")}
        >
          <dt
            className="shrink-0 text-ink-2"
            style={layout === "columns" ? { width: labelWidth } : undefined}
          >
            {item.label}
          </dt>
          <dd className={cx("m-0 min-w-0", TONE[item.tone ?? "ink"])}>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
